// src/lib/auth/roles.js

// Canonical roles (add more at any time)
export const ROLES = {
  ADMIN: 'admin',
  MANAGEMENT: 'management',
  HEAD_HOST: 'head_host',
  HOST: 'host',
  AMBASSADOR: 'ambassador',
  DJ: 'dj',

  // tenant / brand roles (non-hierarchical)
  EMERSON: 'emerson',
  YOURS_TRULY: 'yours_truly',
  BAR_BAMBI: 'bar_bambi'
};

export const ADMIN_MENU_MODES = {
  FULL: 'full',
  MANAGEMENT: 'management'
};

// Per-user access-grant flags: boolean fields stored on the Firestore `users` doc that unlock a
// specific menu item / route for that one user WITHOUT changing their role/type. A menu item or
// ProtectedRoute lists the flags that grant it via `allowedProfileFlags`, and access is granted if
// the user's role passes OR any listed flag is truthy on their profile.
export const ACCESS_FLAGS = {
  // Set `canAccessManagementFrees: true` on a user doc to give an ambassador (or any role) access
  // to the "Add Management Frees" page without promoting them to management.
  MANAGEMENT_FREE_LIST: 'canAccessManagementFrees'
};

// Hierarchical roles in order of privilege (index 0 = most privileged)
const HIERARCHICAL = [
  ROLES.ADMIN,
  ROLES.MANAGEMENT,
  ROLES.HEAD_HOST,
  ROLES.AMBASSADOR,
  ROLES.HOST,
  ROLES.DJ
];

export function normalizeRole(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();

  // hierarchical synonyms
  if (['admin', 'administrator'].includes(s)) return ROLES.ADMIN;
  if (['management', 'manager'].includes(s)) return ROLES.MANAGEMENT;
  if (['head host', 'head hosts', 'head_host', 'head-host'].includes(s)) return ROLES.HEAD_HOST;
  if (['host', 'hosts'].includes(s)) return ROLES.HOST;
  if (['ambassador', 'ambassadors'].includes(s)) return ROLES.AMBASSADOR;
  if (['dj', 'djs'].includes(s)) return ROLES.DJ;

  // tenant / brand synonyms
  if (['emerson'].includes(s)) return ROLES.EMERSON;
  if (['yours truly', 'yourstruly', 'yours_truly', 'yt'].includes(s)) return ROLES.YOURS_TRULY;
  if (['bar bambi', 'barbambi', 'bar_bambi', 'bambi'].includes(s)) return ROLES.BAR_BAMBI;

  return null;
}

export function normalizeAdminMenuMode(raw) {
  if (!raw) return ADMIN_MENU_MODES.FULL;
  const s = String(raw).trim().toLowerCase();

  if (['management', 'manager', 'managed', 'limited', 'clean'].includes(s)) {
    return ADMIN_MENU_MODES.MANAGEMENT;
  }

  return ADMIN_MENU_MODES.FULL;
}

export function menuItemHiddenByAdminMode(node, userRoleRaw, adminMenuModeRaw) {
  const userRole = normalizeRole(userRoleRaw);
  if (userRole !== ROLES.ADMIN) return false;

  return (
    normalizeAdminMenuMode(adminMenuModeRaw) === ADMIN_MENU_MODES.MANAGEMENT &&
    node?.hideForManagementAdminMenu === true
  );
}

function isHierarchical(role) {
  return HIERARCHICAL.includes(role);
}

/**
 * roleIsAllowed(userRoleRaw, requiredRoles)
 * - Admin sees EVERYTHING (first check).
 * - Hierarchical roles inherit (admin > management > head_host > host > ambassador > dj).
 * - Tenant roles (emerson, yours_truly, bar_bambi) require exact match.
 * - Mixed checks (hierarchical vs tenant) only pass if exact match, except admin override.
 */
export function roleIsAllowed(userRoleRaw, requiredRoles) {
  // No required roles = public page / menu item -> everyone can see it
  if (!requiredRoles || requiredRoles.length === 0) return true;

  const userRole = normalizeRole(userRoleRaw);
  if (!userRole) return false;

  // === Admin override: admin can see ALL pages/menu items ===
  if (userRole === ROLES.ADMIN) return true;

  // === Management: must be explicitly included; NO hierarchical inheritance ===
  if (userRole === ROLES.MANAGEMENT) {
    for (const rr of requiredRoles) {
      const req = normalizeRole(rr);
      if (!req) continue;
      if (req === ROLES.MANAGEMENT) return true;
    }
    return false;
  }

  // Everyone else: keep your existing hierarchical / tenant logic
  for (const rr of requiredRoles) {
    const req = normalizeRole(rr);
    if (!req) continue;

    const reqH = isHierarchical(req);
    const userH = isHierarchical(userRole);

    if (reqH && userH) {
      // hierarchical roles: admin > management > head_host > ambassador > host > dj
      const iu = HIERARCHICAL.indexOf(userRole);
      const ir = HIERARCHICAL.indexOf(req);
      if (iu !== -1 && ir !== -1 && iu <= ir) return true; // user outranks or equals
    } else if (!reqH && !userH) {
      // both tenant roles: exact match
      if (userRole === req) return true;
    }
    // mixed (tenant vs hierarchical): no, except admin (handled above)
  }

  return false;
}

// Truthy test for a per-user access flag. Accepts real booleans plus the common stringy values a
// spreadsheet/CMS import might produce ("true", "TRUE", "yes", 1) so a hand-set flag still works.
export function isAccessFlagEnabled(value) {
  if (value === true) return true;
  const s = String(value ?? '')
    .trim()
    .toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

/**
 * profileHasAnyFlag(profile, flagKeys)
 * Returns true if any of the given flag fields is enabled on the user's profile doc.
 * Safe with null/undefined profile or flagKeys.
 */
export function profileHasAnyFlag(profile, flagKeys) {
  if (!profile || !Array.isArray(flagKeys) || flagKeys.length === 0) return false;
  return flagKeys.some((key) => isAccessFlagEnabled(profile?.[key]));
}

/**
 * accessIsAllowed(userRoleRaw, requiredRoles, profile, allowedProfileFlags)
 * Combined gate used by menu filtering and route guards: access is granted when the user's role
 * passes `roleIsAllowed` OR when any per-user access flag in `allowedProfileFlags` is truthy on the
 * profile. This lets specific users be granted a page without a role upgrade.
 */
export function accessIsAllowed(userRoleRaw, requiredRoles, profile, allowedProfileFlags) {
  if (roleIsAllowed(userRoleRaw, requiredRoles)) return true;
  return profileHasAnyFlag(profile, allowedProfileFlags);
}
